import UPNG from "upng-js";

export type Orientation = "portrait" | "landscape";
export type Parity = "even" | "odd";

export const TARGET_DIMS: Record<Orientation, { w: number; h: number }> = {
  portrait: { w: 1664, h: 2432 },
  landscape: { w: 2432, h: 1664 },
};

export interface ConvertInput {
  rgba: Uint8ClampedArray;
  protectedAlpha: Uint8Array;
  width: number;
  height: number;
}

export interface ConvertOptions {
  parity?: Parity;
  colors?: number;
}

export interface ConvertResult {
  pngBuffer: ArrayBuffer;
  width: number;
  height: number;
}

export function detectOrientation(width: number, height: number): Orientation {
  return height >= width ? "portrait" : "landscape";
}

export function convertToCheckerPng(
  input: ConvertInput,
  options: ConvertOptions = {}
): ConvertResult {
  const { parity = "even", colors = 256 } = options;
  const { rgba, protectedAlpha, width: w, height: h } = input;

  // Only "single transparent palette color" indexed PNGs are verified to survive
  // X's re-encode pipeline (spec §2); multi-level tRNS alpha is unverified, so
  // semi-transparent source pixels are snapped to either opaque or transparent.
  for (let p = 0; p < rgba.length; p += 4) {
    if (rgba[p + 3] < 128) {
      rgba[p] = 0;
      rgba[p + 1] = 0;
      rgba[p + 2] = 0;
      rgba[p + 3] = 0;
    } else {
      rgba[p + 3] = 255;
    }
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (protectedAlpha[i] > 0) continue;
      const parityEven = ((x + y) & 1) === 0;
      const visible = parity === "even" ? parityEven : !parityEven;
      if (!visible) {
        const p = i * 4;
        rgba[p] = 0;
        rgba[p + 1] = 0;
        rgba[p + 2] = 0;
        rgba[p + 3] = 0;
      }
    }
  }

  const buf = new Uint8Array(rgba.length);
  buf.set(rgba);
  const pngBuffer = UPNG.encode([buf.buffer], w, h, colors);
  return { pngBuffer, width: w, height: h };
}

export function downloadPng(buffer: ArrayBuffer, filename: string): void {
  const blob = new Blob([buffer], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
